import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import ListBulletedIcon from '@atlaskit/icon/core/list-bulleted';
import DataFlowIcon from '@atlaskit/icon/core/data-flow';
import NoteIcon from '@atlaskit/icon/core/note';

import {
	useSourceAIActions,
	useSurfaceAIActions,
	type BaseAIAction,
} from '@atlassian/ai-smart-button-api';
import { AIDynamicSurface, DYNAMIC_SURFACE_PANEL_ID, RovoLogo } from '@atlassian/ai-smart-button';

import { useCreateObjectSidebarProp } from '@confluence/ai-floating-context-menu';
import {
	useIsObjectSidebarShown,
	usePanel,
	useObjectSidebarActions,
	type ObjectSidebarPanel,
} from '@confluence/object-sidebar-api';
import type {
	WhiteboardSmartActions,
	WhiteboardAIAgentServiceInterface,
} from '@confluence/whiteboard-types/entry-points/WhiteboardAIAgentService';
import {
	CreatePageButtonContextProvider,
	QuickSummaryStreamingComponent,
} from '@confluence/quick-summary';
import { useSessionData } from '@confluence/session-data';

import { whiteboardAIAgentService } from './services/whiteboard-ai-agent-service';

const i18n = defineMessages({
	panelHeaderLabel: {
		id: 'whiteboard-common.use-whiteboard-ai-agent-service.ai-panel-header-name',
		defaultMessage: 'Whiteboard AI Panel',
		description: 'The header label for the dynamic panel created for whiteboards ai agent',
	},
	summarizeWhiteboardButtonTitle: {
		id: 'whiteboard-common.use-whiteboard-ai-agent-service.ai-panel.summarize-button',
		defaultMessage: 'Summarize whiteboard',
		description:
			'Button label for summarize whiteboard action on the whiteboard AI Agent right panel',
	},
	generateDiagramButtonTitle: {
		id: 'whiteboard-common.use-whiteboard-ai-agent-service.ai-panel.generate-diagram-button',
		defaultMessage: 'Generate diagram',
		description: 'Button label for generate diagram action on the whiteboard AI Agent right panel',
	},
	generateCardButtonTitle: {
		id: 'whiteboard-common.use-whiteboard-ai-agent-service.ai-panel.generate-card-button',
		defaultMessage: 'Generate Card',
		description: 'Button label for generate card action on the whiteboard AI Agent right panel',
	},
});

export function AIDynamicSurfaceWrapper() {
	const objectSidebar = useCreateObjectSidebarProp();

	return <AIDynamicSurface objectSidebar={objectSidebar} />;
}

export const dynamicSurfacePanel: ObjectSidebarPanel = {
	id: DYNAMIC_SURFACE_PANEL_ID,
	headerComponentElements: {
		HeaderIcon: RovoLogo,
	},
	BodyComponent: AIDynamicSurfaceWrapper,
};

const WHITEBOARD_DYNAMIC_SURFACE_SOURCE_ID = 'whiteboard';

export type UseWhiteboardAIAgentServiceProps = {
	contentId: string;
	spaceKey: string;
	contentTitle: string;
};

export const useWhiteboardAIAgentService = ({
	contentId,
	spaceKey,
	contentTitle,
}: UseWhiteboardAIAgentServiceProps): WhiteboardAIAgentServiceInterface => {
	const { formatMessage } = useIntl();
	const isObjectSidebarShown = useIsObjectSidebarShown();
	const panel = usePanel();
	const { showObjectSidebar, hideObjectSidebar } = useObjectSidebarActions();
	const [availableWhiteboardSmartActions, setAvailableWhiteboardSmartActions] = useState<string[]>(
		[],
	);
	const [isAgentEnabled, setIsAgentEnabled] = useState(false);
	const { setActions } = useSourceAIActions(WHITEBOARD_DYNAMIC_SURFACE_SOURCE_ID);
	const { cloudId } = useSessionData();
	const { setSurfaceMetadata } = useSurfaceAIActions();

	const summarizeButtonText = formatMessage(i18n.summarizeWhiteboardButtonTitle);
	const generateDiagramButtonText = formatMessage(i18n.generateDiagramButtonTitle);
	const generateCardButtonText = formatMessage(i18n.generateCardButtonTitle);

	useEffect(() => {
		setSurfaceMetadata({ siteId: cloudId, product: 'confluence', contentType: 'whiteboard' });
	}, [cloudId, setSurfaceMetadata]);

	// Handler for calling whiteboards to toggle the ai agent dialog
	const openAIAgentDialog = useCallback(
		(selectedAction: WhiteboardSmartActions) => {
			whiteboardAIAgentService.toggleAIAgentDialog({ selectedAction });
			hideObjectSidebar();
		},
		[hideObjectSidebar],
	);

	const confluenceSideWhiteboardSmartActions: { [key: string]: BaseAIAction } = useMemo(
		() => ({
			summary: {
				id: 'summarize-whiteboard-ai-action',
				title: summarizeButtonText,
				icon: <ListBulletedIcon label="" />,
				viewConfig: {
					render: () => (
						<CreatePageButtonContextProvider
							contentId={contentId}
							spaceKey={spaceKey}
							contentTitle={contentTitle}
						>
							<QuickSummaryStreamingComponent
								contentId={contentId}
								spaceKey={spaceKey}
								contentType="whiteboard"
								lastModifiedDate=""
								source="whiteboard"
								setIsOpen={() => {}}
								contentPath={window.location.pathname}
								isInObjectSidebar
							/>
						</CreatePageButtonContextProvider>
					),
				},
			},
		}),
		[summarizeButtonText, contentId, spaceKey, contentTitle],
	);

	const whiteboardSideWhiteboardSmartActions: { [key: string]: BaseAIAction } = useMemo(
		() => ({
			diagram: {
				id: 'generate-diagram-whiteboard-ai-action',
				title: generateDiagramButtonText,
				onClick: () => openAIAgentDialog('diagram'),
				icon: <DataFlowIcon label="" />,
			},
			card: {
				id: 'generate-card-whiteboard-ai-action',
				title: generateCardButtonText,
				onClick: () => openAIAgentDialog('card'),
				icon: <NoteIcon label="" />,
			},
		}),
		[generateCardButtonText, generateDiagramButtonText, openAIAgentDialog],
	);

	// Clean up
	useEffect(() => {
		return () => {
			whiteboardAIAgentService.dispose();
		};
		// clean-up on unmount
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Register for smart action updates
	useEffect(() => {
		whiteboardAIAgentService.registerOnSmartActionsUpdate(setAvailableWhiteboardSmartActions);
	}, [setAvailableWhiteboardSmartActions]);

	useEffect(() => {
		const filteredActions = isAgentEnabled
			? availableWhiteboardSmartActions.map(
					(action) => whiteboardSideWhiteboardSmartActions[action],
				)
			: [];
		setActions([
			...Object.values(confluenceSideWhiteboardSmartActions),
			...Object.values(filteredActions),
		]);
	}, [
		confluenceSideWhiteboardSmartActions,
		availableWhiteboardSmartActions,
		whiteboardSideWhiteboardSmartActions,
		setActions,
		isAgentEnabled,
	]);

	// Object sidebar handlers
	const toggleWhiteboardAIAgentObjectSidePanel = useCallback(() => {
		if (isObjectSidebarShown && panel?.id === dynamicSurfacePanel.id) {
			hideObjectSidebar();
		} else {
			showObjectSidebar(dynamicSurfacePanel, 'push');
		}
	}, [isObjectSidebarShown, panel, hideObjectSidebar, showObjectSidebar]);

	useEffect(() => {
		whiteboardAIAgentService.registerToggleWhiteboardAIAgentRightPanel(
			toggleWhiteboardAIAgentObjectSidePanel,
		);
	}, [toggleWhiteboardAIAgentObjectSidePanel]);

	useEffect(() => {
		whiteboardAIAgentService.updateConfluenceSidebarState({
			isOpen: isObjectSidebarShown,
			panelId: panel?.id || '',
		});
	}, [isObjectSidebarShown, panel?.id]);

	useEffect(() => {
		whiteboardAIAgentService.onAgentEnablementChange(setIsAgentEnabled);
	}, [setIsAgentEnabled]);

	return whiteboardAIAgentService;
};
